import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { FocusTrapFactory } from '@angular/cdk/a11y';
import { ComponentPortal } from '@angular/cdk/portal';
import {
  AccessibleOverlayHandle,
  makeOverlayAccessible
} from '../overlay/accessible-overlay';
import {
  ComponentRef,
  computed,
  Injectable,
  Injector,
  signal,
  inject
} from '@angular/core';
import { debounceTime, firstValueFrom, noop, Subscription } from 'rxjs';
// Type-only import so the overlay component isn't pulled into the eager
// bundle; the class is fetched via dynamic import on first open.
import type { AceUploadSidebarOverlayComponent } from '@cpd-app/core-module/components/ace-upload-sidebar/ace-upload-sidebar-overlay.component';
import {
  AceApiService,
  AceCredentialRow,
  AceDryRunResponse,
  AceTranscriptSummary,
} from './ace-api.service';

// Upload-flow state machine. Owned by the service (not the overlay) so state
// survives panel close/reopen. 'type-select' is the doc-type picker (JST vs
// ACE) shown only when the ACE flag is on; 'list' is the multiplicity
// read-back of prior ACE transcripts.
export type AceViewState =
  | 'type-select'
  | 'upload'
  | 'parsing'
  | 'confirm'
  | 'saving'
  | 'saved'
  | 'list'
  | 'error';

@Injectable({
  providedIn: 'root',
})
export class AceSidebarService {
  private readonly isSidebarOpen$ = signal<boolean>(false);
  private overlayRef!: OverlayRef;
  // Accessibility behavior for the raw CDK overlay; released on detach.
  private a11y: AccessibleOverlayHandle | null = null;
  private portal: ComponentPortal<AceUploadSidebarOverlayComponent> | null =
    null;
  private componentRef: ComponentRef<AceUploadSidebarOverlayComponent> | null =
    null;
  // The CDK ComponentPortal is reused across opens; EventEmitter subscribers
  // aren't cleared on detach, so we explicitly tear down the prior
  // subscription each open to avoid stacking close handlers.
  private closedSidebarSubscription: Subscription | null = null;

  public readonly isSidebarOpen = computed(() => this.isSidebarOpen$());

  // Upload-flow state. Lives on the service so an in-flight parse + its result
  // survive the panel being dismissed mid-parse.
  public readonly viewState = signal<AceViewState>('type-select');
  public readonly errorMessage = signal<string | null>(null);
  public readonly parsedResult = signal<AceDryRunResponse | null>(null);
  public readonly editableCredentials = signal<AceCredentialRow[]>([]);

  // Multiplicity: the user's previously-confirmed transcripts, shown on the
  // 'list' state and refreshed after each save.
  public readonly savedTranscripts = signal<AceTranscriptSummary[]>([]);

  constructor(
    private readonly overlay: Overlay,
    private readonly injector: Injector,
    private readonly aceApiService: AceApiService,
    private readonly focusTrapFactory: FocusTrapFactory
  ) {}

  public async openAceSidebar(): Promise<void> {
    if (!this.portal) {
      const componentModule = await import(
        '@cpd-app/core-module/components/ace-upload-sidebar/ace-upload-sidebar-overlay.component'
      );
      this.portal = new ComponentPortal(
        componentModule.AceUploadSidebarOverlayComponent,
        null,
        this.injector
      );
    }

    if (!this.overlayRef) {
      this.createOverlay();
    }

    if (this.overlayRef.hasAttached()) {
      this.closeAceSidebar();
      return;
    }

    // Fresh open from a terminal screen: start back at the doc-type picker
    // rather than re-showing a stale 'saved'/'error' view. Mid-flow states
    // (upload/confirm/list) are preserved so an interrupted parse — which
    // reopenIfDetached re-attaches on 'confirm'/'error-from-parse' — resumes.
    if (this.viewState() === 'saved') {
      this.resetFlow();
    }

    this.isSidebarOpen$.set(true);
    this.componentRef = this.overlayRef.attach(this.portal);
    this.a11y = makeOverlayAccessible(this.overlayRef, this.focusTrapFactory, {
      ariaLabel: 'Upload a document',
      onEscape: () => this.closeAceSidebar()
    });
    this.closedSidebarSubscription?.unsubscribe();
    this.closedSidebarSubscription = this.componentRef.instance.closedSidebar
      .pipe(debounceTime(500))
      .subscribe({
        next: () => this.closeAceSidebar(),
        error: noop,
      });
  }

  /**
   * The user picked "ACE transcript" on the doc-type picker. Fetch their prior
   * transcripts: if any exist, land on the multiplicity list; otherwise go
   * straight to the upload screen. Failures fall through to the upload screen
   * (the list is a convenience, not a gate).
   */
  public async chooseAce(): Promise<void> {
    try {
      const result = await firstValueFrom(this.aceApiService.listTranscripts());
      const transcripts = result?.success ? result.transcripts : [];
      this.savedTranscripts.set(transcripts);
      this.viewState.set(transcripts.length > 0 ? 'list' : 'upload');
    } catch {
      this.savedTranscripts.set([]);
      this.viewState.set('upload');
    }
  }

  /** Kick off the dryRun upload; mirrors the JST parse lifecycle. */
  public startUpload(file: File): void {
    // Guard against a double-submit re-entering while a parse is in flight.
    if (this.viewState() === 'parsing') {
      return;
    }
    this.viewState.set('parsing');
    this.errorMessage.set(null);
    this.aceApiService.uploadDryRun(file).subscribe({
      next: (response) => {
        this.parsedResult.set(response);
        this.editableCredentials.set(response.data?.items ?? []);
        this.viewState.set('confirm');
        this.reopenIfDetached();
      },
      error: (err) => {
        const message = err?.error?.message ?? err?.message ?? 'Unknown error';
        this.errorMessage.set(message);
        this.viewState.set('error');
        this.reopenIfDetached();
      },
    });
  }

  /** Refresh the saved-transcripts list (called after a successful confirm). */
  public async refreshTranscripts(): Promise<void> {
    try {
      const result = await firstValueFrom(this.aceApiService.listTranscripts());
      this.savedTranscripts.set(result?.success ? result.transcripts : []);
    } catch {
      // Leave the existing list in place; non-fatal.
    }
  }

  /** Start a fresh upload from the saved/list screens. */
  public startAnother(): void {
    this.parsedResult.set(null);
    this.editableCredentials.set([]);
    this.errorMessage.set(null);
    this.viewState.set('upload');
  }

  /** Reset everything back to the picker (used on full close/reset). */
  public resetFlow(): void {
    this.viewState.set('type-select');
    this.errorMessage.set(null);
    this.parsedResult.set(null);
    this.editableCredentials.set([]);
  }

  private reopenIfDetached(): void {
    if (this.overlayRef && !this.overlayRef.hasAttached()) {
      this.openAceSidebar();
    }
  }

  public closeAceSidebar(): void {
    this.isSidebarOpen$.set(false);
    this.closedSidebarSubscription?.unsubscribe();
    this.closedSidebarSubscription = null;
    if (this.overlayRef?.hasAttached()) {
      this.a11y?.release();
      this.a11y = null;
      this.overlayRef.detach();
    }
  }

  private createOverlay(): void {
    this.overlayRef = this.overlay.create({
      hasBackdrop: true,
      panelClass: 'left-sidebar-panel',
      backdropClass: 'cdk-overlay-blur-backdrop',
      positionStrategy: this.overlay.position().global().left('0').top('0'),
    });

    this.overlayRef
      .backdropClick()
      .subscribe({ next: () => this.closeAceSidebar(), error: noop });
  }
}
